import RiskMatrix from "../RiskMatrix";
import UserField from "kinnect-front/User/UserField";
import { FetchesData } from "kinnect-mixins";
import { SafetyNearMissReportType, User } from "kinnect-models";
import {
	KAutocomplete,
	KDatePicker,
	KForm,
	KTextField,
	KTextarea,
	KTimePicker
} from "kinnect-general";
import { VCol, VRow } from "vuetify/lib";
import { compareAsc, parseISO } from "date-fns";

export default KForm.extend( {
	name: "KNearMissReportForm",

	props: {
		redirect: {
			...KForm.options.props.redirect,
			default: () => [ "back", { name: "safetyNearMisses" } ]
		},

		report: Object
	},

	mixins: [ FetchesData ],

	data: () => ( {
		formFields: {
			completedDate: null,
			correctivePlan: null,
			employeeComments: null,
			idReportType: null,
			idUserSupervisor: null,
			idsEmployeesInvolved: [],
			immediatePlan: null,
			incidentDate: null,
			incidentDescription: null,
			incidentLocation: null,
			incidentTime: null,
			projectedPlanCompletionDate: null,
			reportedDate: null,
			riskProbability: null,
			riskSeverity: null
		},
		riskAssessment: null,
		dateCreated: null,
		nearMissReportTypes: [],
		today: ( new Date() ).setHours( 0, 0, 0, 0 ),
		users: []
	} ),

	computed: {
		editing() {
			return Boolean( this.report );
		},

		formUrl() {
			return this.editing
				? `safety/near-miss-reports/${this.report.id}`
				: "safety/near-miss-reports";
		}
	},

	watch: {
		report: {
			handler( report ) {
				if ( report ) {
					Object.keys( this.formFields ).forEach( key => {
						if ( report.hasOwnProperty( key ) ) {
							this.formFields[key] = report[key];
						}
					} );

					this.dateCreated = report.created_at;

					this.formFields.idsEmployeesInvolved = report.employeesInvolved.map( item => item.id );
				}
			},
			immediate: true
		}
	},

	methods: {
		isNotPastDate( date ) {
			return compareAsc( parseISO( date ), this.today ) !== -1;
		},

		isNotFutureDate( date ) {
			return compareAsc( this.today, parseISO( date ) ) !== -1;
		},

		async fetchUsers() {
			this.users = await User.where( "approved", true ).get();
		},

		async fetchReportTypes() {
			this.nearMissReportTypes = await SafetyNearMissReportType.get();
		},

		async fetch() {
			await this.$http.all( [
				this.fetchUsers(),
				this.fetchReportTypes()
			] );
		}
	},

	render() {
		return <KForm
			attrs={{
				...this.$props,
				action: this.formUrl,
				data: this.formFields,
				method: this.editing ? "put" : "post"
			}}
			on={this.$listeners}
		>
			<KDatePicker
				disabled
				textFieldProps={{ label: "Date of Report*" }}
				value={this.editing ? this.dateCreated : this.today}
			></KDatePicker>

			<KAutocomplete
				itemText="name"
				itemValue="id"
				items={this.nearMissReportTypes}
				label="Report Type*"
				loading={this.$wait.is( "get near miss report types" )}
				required
				vModel={this.formFields.idReportType}
				validationName="report type"
				validationRules="required"
			></KAutocomplete>

			<UserField
				deletableChips
				items={this.users}
				label="Employee(s) Involved*"
				loading={this.$wait.is( "get users" )}
				multiple
				noDataText="No employees to display"
				required
				vModel={this.formFields.idsEmployeesInvolved}
				validationName="employees involved"
				validationRules="required"
			></UserField>

			<KTextField
				label="Location of Incident*"
				required
				vModel={this.formFields.incidentLocation}
				validationName="incident location"
				validationRules="required"
			></KTextField>

			<VRow>
				<VCol class="py-0" cols={12} sm={6}>
					<KDatePicker
						datePickerProps={{ allowedDates: this.isNotFutureDate }}
						textFieldProps={{
							label: "Date of Incident*",
							required: true,
							validationName: "date of incident",
							validationRules: "required"
						}}
						vModel={this.formFields.incidentDate}
					></KDatePicker>
				</VCol>

				<VCol class="py-0" cols={12} sm={6}>
					<KTimePicker
						textFieldProps={{
							label: "Time of Incident*",
							required: true,
							validationName: "time of incident",
							validationRules: "required"
						}}
						vModel={this.formFields.incidentTime}
					></KTimePicker>
				</VCol>
			</VRow>

			<KTextarea
				label="Description of Incident*"
				required
				vModel={this.formFields.incidentDescription}
				validationName="description of incident"
				validationRules="required"
			></KTextarea>

			<KTextarea
				label="Employee's Comments"
				vModel={this.formFields.employeeComments}
			></KTextarea>

			<VRow>
				<VCol class="py-0" cols={12} sm={6}>
					<KDatePicker
						datePickerProps={{ allowedDates: this.isNotFutureDate }}
						textFieldProps={{
							label: "Date Reported to Supervisor*",
							required: true,
							validationName: "date reported to supervisor",
							validationRules: "required"
						}}
						vModel={this.formFields.reportedDate}
					></KDatePicker>
				</VCol>

				<VCol class="py-0" cols={12} sm={6}>
					<UserField
						items={this.users}
						label="Supervisor*"
						loading={this.$wait.is( "get users" )}
						noDataText="No users to display"
						required
						smallChips
						vModel={this.formFields.idUserSupervisor}
						validationName="supervisor"
						validationRules="required"
					></UserField>
				</VCol>
			</VRow>

			<KTextarea
				label="Immediate Plan/Action Taken*"
				required
				vModel={this.formFields.immediatePlan}
				validationName="immediate plan/action taken"
				validationRules="required"
			></KTextarea>

			<KTextarea
				label="Corrective Plan/Action*"
				required
				vModel={this.formFields.correctivePlan}
				validationName="corrective plan/action"
				validationRules="required"
			></KTextarea>

			<KDatePicker
				datePickerProps={{ allowedDates: this.isNotPastDate }}
				textFieldProps={{
					label: "Projected Corrective Plan/Action Completion Date*",
					required: true,
					validationName: "projected corrective plan/action completion date",
					validationRules: "required"
				}}
				vModel={this.formFields.projectedPlanCompletionDate}
			></KDatePicker>

			<KDatePicker
				datePickerProps={{ allowedDates: this.isNotFutureDate }}
				textFieldProps={{ label: "Date Completed" }}
				vModel={this.formFields.completedDate}
			></KDatePicker>

			<p class="text-h4 text-center mt-5">Risk Assessment</p>

			<RiskMatrix
				probability={this.formFields.riskProbability}
				required
				severity={this.formFields.riskSeverity}
				validationName="risk assessment"
				validationRules="required"
				{...{
					on: {
						"update:probability": val => this.formFields.riskProbability = val,
						"update:severity": val => this.formFields.riskSeverity = val
					}
				}}
			></RiskMatrix>
		</KForm>;
	}
} );
